import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { useAppSelector } from "redux/hooks";
import { selectMyUserId } from "redux/slices/session";
import { UserInfo, UserType } from "generated/graphql";
import { displayUserType, parseSafeInt } from "Backend";
import theme from "theme";
import { Link } from "react-router-dom";
import { P } from "theme/typography";
import { fullName, fullNameFromParts } from "helpers/name-helpers";
import { useMemo, useState } from "react";
import PSelect from "components/PSelect";
import { selectLenses } from "redux/slices/client";

export interface UsersTableProps {
  users: UserInfo[];
}

const UsersTable = (props: UsersTableProps) => {
  const { users } = props;
  const myId = useAppSelector(selectMyUserId);
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      if (a.deactivated && !b.deactivated) return 1;
      if (!a.deactivated && b.deactivated) return -1;
      return fullName(a).localeCompare(fullName(b));
    });
  }, [users]);
  const lenses = useAppSelector(selectLenses);
  const [filterSearch, setFilterSearch] = useState("");
  const [filterUserType, setFilterUserType] = useState<UserType | undefined>(undefined);
  const [filterLens, setFilterLens] = useState<number | undefined>(undefined);
  const filteredUsers = sortedUsers.filter((user) => {
    const search = filterSearch.toLocaleLowerCase();
    const searchMet =
      user.email.toLocaleLowerCase().indexOf(search) > -1 ||
      fullNameFromParts(user.firstName, user.lastName).toLocaleLowerCase().indexOf(search) > -1;
    if (filterUserType !== undefined && user.userType !== filterUserType) {
      return false;
    }
    if (!searchMet) {
      return false;
    }
    if (filterLens !== undefined && !user.lenses.find((l) => l.id === filterLens)) {
      return false;
    }
    return true;
  });

  return (
    <Card className="mt-4 flex-1 overflow-auto">
      <CardContent className="flex flex-col h-full">
        <div className="flex gap-2 items-center">
          <FormControl size="small" sx={{ width: 250 }}>
            <TextField
              value={filterSearch}
              onChange={(evt) => setFilterSearch(evt.target.value || "")}
              label="Search"
              placeholder="Search name or email"
            />
          </FormControl>
          <FormControl size="small" sx={{ width: 150 }}>
            <InputLabel variant="outlined">Status</InputLabel>
            <PSelect
              data-pendo="user-table-filter-usertype"
              label="Status"
              value={filterUserType || ""}
              onChange={(evt) => {
                const v = evt.target.value as UserType;
                setFilterUserType(v || undefined);
              }}>
              <MenuItem value="">Show All</MenuItem>
              <MenuItem value={UserType.Admin}>Admin</MenuItem>
              <MenuItem value={UserType.Reviewer}>Reviewer</MenuItem>
            </PSelect>
          </FormControl>
          <FormControl size="small" sx={{ width: 250 }}>
            <InputLabel variant="outlined">Lens</InputLabel>
            <PSelect
              data-pendo="user-table-filter-lens"
              label="Lens"
              value={filterLens?.toString() ?? ""}
              onChange={(evt) => {
                const v = evt.target.value as string;
                setFilterLens(parseSafeInt(v));
              }}>
              <MenuItem value="">Show All</MenuItem>
              {lenses.map((lens) => (
                <MenuItem key={lens.id} value={lens.id.toString()}>
                  {lens.name}
                </MenuItem>
              ))}
            </PSelect>
          </FormControl>
        </div>
        <TableContainer sx={{ height: "100%" }}>
          <Table stickyHeader sx={{ tableLayout: { xs: "fixed" } }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                {!isMobile && (
                  <>
                    <TableCell>Join Date</TableCell>
                    <TableCell>Role</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => {
                const isMe = user.id === myId;
                return (
                  <TableRow hover key={user.email} selected={false}>
                    <TableCell>
                      <Link to={`${user.id}`}>
                        <P
                          sx={{
                            ...theme.mixins.linkLike,
                          }}>
                          {isMe
                            ? `${user.firstName} ${user.lastName} (You)`
                            : `${user.firstName ?? ""} ${user.lastName ?? ""}`}
                        </P>
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        overflow: { xs: "hidden" },
                        textOverflow: { xs: "ellipsis" },
                      }}>
                      {user.email}
                    </TableCell>
                    {!isMobile && (
                      <>
                        <TableCell>{format(parseISO(user.createdAt), "MM/dd/yyyy")}</TableCell>
                        <TableCell>{user.deactivated ? "Deactivated" : displayUserType(user.userType)}</TableCell>
                        {/*<TableCell>*/}
                        {/*  <Checkbox disabled checked={user.admin} />*/}
                        {/*</TableCell>*/}
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default UsersTable;
